import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
  Badge,
  Collapse,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PrinterOutlined,
  UploadOutlined,
  UserAddOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { finalInspectionReportService } from '@/services/final-inspection-report.service';
import type {
  FinalInspectionReport,
  ApproveFIRRequest,
  FirInspectionEquipment,
} from '@/services/final-inspection-report.service';
import assetService, { type AssetDto } from '@/services/asset.service';
import { tmmdeStatusLabel } from '@/pages/tmmde/tmmdeDisplay';
import { useTmmdeUseOverride } from '@/hooks/useTmmdeUseOverride';
import { firRequestService } from '@/services/qc-workflow.service';
import type { FinalInspectionRequest, FIRLineItemResult, FirRequestAttachment } from '@/services/qc-workflow.service';
import workOrderService from '@/services/workOrder.service';
import InspectionWorkLineDetailsCard from '@/components/quality/InspectionWorkLineDetailsCard';
import { useUserDisplayResolver } from '@/hooks/useUserDisplayResolver';
import { getFirPlannedQuantity, isFirApproveQuantityValid } from '@/utils/firApproveRules';
import { useUsers } from '@/hooks/useUsers';
import { buildUserIdToLabelMap, resolveUserDisplayFromMap } from '@/utils/userDisplay';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;

type FirSource = 'qms_report' | 'fir_request';

function parseSource(raw: string | null): FirSource | null {
  if (raw === 'qms_report' || raw === 'fir_request') return raw;
  return null;
}

function firDerivedApproval(fir: FinalInspectionRequest): FinalInspectionReport['approval_status'] {
  const s = String(fir.status ?? '');
  if (s === 'Approved' || s === 'Partial') return 'Approved';
  if (s === 'Rejected') return 'Rejected';
  return 'Pending Approval';
}

function unwrap<T>(res: unknown): T {
  const r = res as { data?: T };
  return (r?.data !== undefined ? r.data : res) as T;
}

const FinalInspectionReportDetailPage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const source = parseSource(searchParams.get('source'));
  const { resolve } = useUserDisplayResolver();
  const { data: users = [] } = useUsers({ is_active: true });
  const userMap = useMemo(() => buildUserIdToLabelMap(users), [users]);
  const ru = (uid?: string | null) => resolveUserDisplayFromMap(userMap, uid, '—');

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignInspectorId, setAssignInspectorId] = useState<string | undefined>();
  const [qmsApproveOpen, setQmsApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [firRejectOpen, setFirRejectOpen] = useState(false);
  const [firRejectReason, setFirRejectReason] = useState('');
  const [uploadList, setUploadList] = useState<File[]>([]);

  const [approveForm] = Form.useForm();
  const [lineForm] = Form.useForm();
  const [inspectionDetailsForm] = Form.useForm<{
    inspection_criteria?: string;
    equipment_asset_ids?: string[];
  }>();

  const { data: assets = [], isLoading: assetsLoading } = useQuery({
    queryKey: ['assets', 'fir-equipment-picklist'],
    queryFn: () => assetService.list(),
    staleTime: 5 * 60 * 1000,
  });

  const selectedEquipmentIds = Form.useWatch('equipment_asset_ids', inspectionDetailsForm) as string[] | undefined;
  const assetOptions = useMemo(() => {
    const selected = new Set(selectedEquipmentIds || []);
    return assets
      .filter((a: AssetDto) => a.is_measuring_equipment || selected.has(a.id))
      .map((a: AssetDto) => {
        const status = a.calibration_status;
        const usable = status === 'CALIBRATED' || status === 'DUE_SOON';
        return {
          value: a.id,
          label: `${a.asset_code} — ${a.asset_name}${
            a.is_measuring_equipment && !usable ? ` (${tmmdeStatusLabel(status || 'PENDING')})` : ''
          }`,
        };
      });
  }, [assets, selectedEquipmentIds]);

  const detailQuery = useQuery({
    queryKey: ['final-inspection-detail', id, source],
    queryFn: async () => {
      if (!id || !source) throw new Error('Missing route parameters');
      if (source === 'qms_report') {
        return { source, report: await finalInspectionReportService.getById(id) } as const;
      }
        return { source, fir: await firRequestService.getById(id) } as const;
    },
    enabled: !!id && !!source,
  });

  const firForOps =
    detailQuery.data?.source === 'fir_request' ? detailQuery.data.fir : null;
  const reportForOps =
    detailQuery.data?.source === 'qms_report' ? detailQuery.data.report : null;
  const woIdForOps = firForOps?.work_order_id || reportForOps?.work_order_id || '';
  const lineIdForOps =
    firForOps?.work_order_line_item_id ||
    firForOps?.workOrderLineItem?.id ||
    firForOps?.lineItemResults?.[0]?.work_order_line_item_id ||
    '';

  const { data: boqOps, isLoading: boqOpsLoading } = useQuery({
    queryKey: ['work-orders', woIdForOps, 'boq-operations'],
    queryFn: () => workOrderService.getBoqOperations(woIdForOps),
    enabled: Boolean(woIdForOps),
  });

  const boqLineForFir = useMemo(() => {
    if (!boqOps?.lines?.length) return null;
    if (lineIdForOps) {
      return boqOps.lines.find((l) => l.work_order_line_item_id === lineIdForOps) || null;
    }
    return boqOps.lines.length === 1 ? boqOps.lines[0] : null;
  }, [boqOps, lineIdForOps]);

  const firWorkLine = useMemo(() => {
    const fromFir = firForOps?.workOrderLineItem;
    return {
      line_number: fromFir?.line_number ?? boqLineForFir?.line_number ?? null,
      item_code: fromFir?.item_code ?? boqLineForFir?.item_code ?? null,
      item_description:
        fromFir?.item_description ??
        boqLineForFir?.item_description ??
        reportForOps?.item_name ??
        null,
      revision: fromFir?.revision ?? boqLineForFir?.revision ?? null,
      drawing_reference_number: fromFir?.drawing_reference_number ?? null,
      part_number: fromFir?.part_number ?? null,
      part_description: fromFir?.part_description ?? null,
      heat_number: fromFir?.heat_number ?? null,
      planned_quantity: fromFir?.planned_quantity ?? null,
      uom: fromFir?.uom ?? null,
    };
  }, [firForOps?.workOrderLineItem, boqLineForFir, reportForOps?.item_name]);

  const firLineOperations = boqLineForFir?.operations || [];

  const assignMutation = useMutation({
    mutationFn: async () => {
      if (!assignInspectorId) throw new Error('Select an inspector');
      await firRequestService.assignInspector(id, assignInspectorId);
    },
    onSuccess: () => {
      message.success('Inspector assigned');
      setAssignOpen(false);
      setAssignInspectorId(undefined);
      queryClient.invalidateQueries({ queryKey: ['final-inspection-detail', id, source] });
      queryClient.invalidateQueries({ queryKey: ['final-inspection-reports'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Failed to assign inspector'),
  });

  const recordLineMutation = useMutation({
    mutationFn: async (payload: {
      line_item_id: string;
      approved_quantity: number;
      rejected_quantity: number;
      rejection_reason?: string;
    }) => {
      const res = await firRequestService.recordLineItemResults(id, [payload]);
      return unwrap<FIRLineItemResult[]>(res);
    },
    onSuccess: () => {
      message.success('Line results saved');
      queryClient.invalidateQueries({ queryKey: ['final-inspection-detail', id, source] });
      queryClient.invalidateQueries({ queryKey: ['final-inspection-reports'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Failed to save line results'),
  });

  const qmsApproveMutation = useMutation({
    mutationFn: async (data: ApproveFIRRequest) => finalInspectionReportService.approve(id, data),
    onSuccess: () => {
      message.success('Report updated');
      setQmsApproveOpen(false);
      approveForm.resetFields();
      queryClient.invalidateQueries({ queryKey: ['final-inspection-detail', id, source] });
      queryClient.invalidateQueries({ queryKey: ['final-inspection-reports'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Failed to submit'),
  });

  const firApproveMutation = useMutation({
    mutationFn: async () => {
      await firRequestService.approve(id);
    },
    onSuccess: () => {
      message.success('FIR approved');
      queryClient.invalidateQueries({ queryKey: ['final-inspection-detail', id, source] });
      queryClient.invalidateQueries({ queryKey: ['final-inspection-reports'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Failed to approve'),
  });

  const firRejectMutation = useMutation({
    mutationFn: async (reason: string) => firRequestService.reject(id, reason),
    onSuccess: () => {
      message.warning('FIR rejected');
      setFirRejectOpen(false);
      setFirRejectReason('');
      queryClient.invalidateQueries({ queryKey: ['final-inspection-detail', id, source] });
      queryClient.invalidateQueries({ queryKey: ['final-inspection-reports'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Failed to reject'),
  });

  const qmsUploadMutation = useMutation({
    mutationFn: (file: File) => finalInspectionReportService.addAttachment(id, file),
    onSuccess: () => {
      message.success('Attachment uploaded');
      queryClient.invalidateQueries({ queryKey: ['final-inspection-detail', id, source] });
      queryClient.invalidateQueries({ queryKey: ['final-inspection-reports'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Upload failed'),
  });

  const firUploadMutation = useMutation({
    mutationFn: (file: File) => firRequestService.addAttachment(id, file),
    onSuccess: () => {
      message.success('Attachment uploaded');
      queryClient.invalidateQueries({ queryKey: ['final-inspection-detail', id, source] });
      queryClient.invalidateQueries({ queryKey: ['final-inspection-reports'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Upload failed'),
  });

  const deleteQmsAtt = useMutation({
    mutationFn: (attachmentId: string) => finalInspectionReportService.deleteAttachment(attachmentId),
    onSuccess: () => {
      message.success('Attachment removed');
      queryClient.invalidateQueries({ queryKey: ['final-inspection-detail', id, source] });
      queryClient.invalidateQueries({ queryKey: ['final-inspection-reports'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Delete failed'),
  });

  const deleteFirAtt = useMutation({
    mutationFn: (attachmentId: string) => firRequestService.deleteAttachment(attachmentId),
    onSuccess: () => {
      message.success('Attachment removed');
      queryClient.invalidateQueries({ queryKey: ['final-inspection-detail', id, source] });
      queryClient.invalidateQueries({ queryKey: ['final-inspection-reports'] });
    },
    onError: (e: any) => message.error(e?.message || 'Delete failed'),
  });

  const { runWithOverride, overrideModal } = useTmmdeUseOverride();
  const saveInspectionDetailsMutation = useMutation({
    mutationFn: async (values: { inspection_criteria?: string; equipment_asset_ids?: string[] }) =>
      runWithOverride(async (overrideReason) => {
        const body = {
          inspection_criteria: values.inspection_criteria?.trim() || null,
          equipment_asset_ids: values.equipment_asset_ids ?? [],
          ...(overrideReason ? { tmmde_override_reason: overrideReason } : {}),
        };
        if (source === 'qms_report') {
          return finalInspectionReportService.updateInspectionDetails(id, body);
        }
        const res = await firRequestService.updateInspectionDetails(id, body);
        return unwrap<FinalInspectionRequest>(res);
      }),
    onSuccess: () => {
      message.success('Inspection details saved');
      queryClient.invalidateQueries({ queryKey: ['final-inspection-detail', id, source] });
      queryClient.invalidateQueries({ queryKey: ['final-inspection-reports'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Save failed'),
  });

  useEffect(() => {
    const d = detailQuery.data;
    if (!d) return;
    const isQmsLocal = d.source === 'qms_report';
    const criteria =
      (isQmsLocal ? d.report?.inspection_criteria : d.fir?.inspection_criteria) ?? '';
    const equipment: FirInspectionEquipment[] =
      (isQmsLocal ? d.report?.inspection_equipment : d.fir?.inspection_equipment) ?? [];
    inspectionDetailsForm.setFieldsValue({
      inspection_criteria: criteria,
      equipment_asset_ids: equipment.map((e) => e.asset_id),
    });
  }, [detailQuery.data, id, source, inspectionDetailsForm]);

  if (!source) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          showIcon
          message="Invalid or missing source"
          description='Open this record from the Final Inspection list with ?source=qms_report or ?source=fir_request.'
          action={
            <Button type="primary" onClick={() => navigate('/qms/final-inspection-reports')}>
              Back to list
            </Button>
          }
        />
      </div>
    );
  }

  if (detailQuery.isLoading || !detailQuery.data) {
    return (
      <div style={{ padding: 120, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (detailQuery.isError) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          showIcon
          message="Could not load this record"
          description={(detailQuery.error as Error)?.message || 'Wrong id/source combination or network error.'}
          action={
            <Button type="primary" onClick={() => navigate('/qms/final-inspection-reports')}>
              Back to list
            </Button>
          }
        />
      </div>
    );
  }

  const data = detailQuery.data;
  const isQms = data.source === 'qms_report';
  const report = isQms ? data.report : null;
  const fir = !isQms ? data.fir : null;

  const inspectionCriteria =
    (isQms ? report?.inspection_criteria : fir?.inspection_criteria) ?? null;
  const inspectionEquipment: FirInspectionEquipment[] = (isQms
    ? report?.inspection_equipment
    : fir?.inspection_equipment) ?? [];

  const printHref = `/print/qms/final-inspection-reports/${id}?source=${source}`;

  const renderInspectionDetailsTab = () => (
    <Card title="Inspection criteria & equipment">
      <Form
        form={inspectionDetailsForm}
        layout="vertical"
        onFinish={(vals) => saveInspectionDetailsMutation.mutate(vals)}
      >
        <Form.Item
          name="inspection_criteria"
          label="Inspection criteria"
          extra="Acceptance criteria, inspection method, and reference standards for this final inspection."
        >
          <TextArea rows={5} placeholder="e.g. Visual and dimensional per drawing; 100% VT of welds…" />
        </Form.Item>
        <Form.Item
          name="equipment_asset_ids"
          label="Inspection equipment used"
          extra="Measuring equipment from the Equipment List. Equipment that is overdue, pending or on hold is flagged — it must not be used for acceptance."
        >
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            loading={assetsLoading}
            placeholder="Select one or more assets"
            options={assetOptions}
          />
        </Form.Item>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          htmlType="submit"
          loading={saveInspectionDetailsMutation.isPending}
        >
          Save inspection details
        </Button>
      </Form>
      {inspectionEquipment.length > 0 && (
        <Table
          style={{ marginTop: 16 }}
          size="small"
          pagination={false}
          rowKey="asset_id"
          dataSource={inspectionEquipment}
          columns={[
            { title: 'Asset code', dataIndex: 'asset_code', key: 'code' },
            { title: 'Asset name', dataIndex: 'asset_name', key: 'name' },
          ]}
        />
      )}
    </Card>
  );

  const openQmsApprove = () => {
    if (report) {
      approveForm.setFieldsValue({
        approved_quantity: report.approved_quantity ?? 0,
        rejected_quantity: report.rejected_quantity ?? 0,
      });
    }
    setQmsApproveOpen(true);
  };

  const submitQmsApprove = async () => {
    try {
      const values = await approveForm.validateFields();
      const approvalData: ApproveFIRRequest = {
        approval_status: 'Approved',
        approval_comments: values.approval_comments || '',
        approval_documents: values.approval_documents || [],
        approved_quantity: values.approved_quantity,
        rejected_quantity: values.rejected_quantity,
      };
      await qmsApproveMutation.mutateAsync(approvalData);
    } catch {
      /* validation */
    }
  };

  const submitQmsReject = async () => {
    if (!report) return;
    try {
      const values = await approveForm.validateFields();
      if (!values.rejection_reason?.trim()) {
        message.error('Please provide a rejection reason');
        return;
      }
      const approvalData: ApproveFIRRequest = {
        approval_status: 'Rejected',
        rejection_reason: values.rejection_reason.trim(),
        approval_comments: values.approval_comments,
        approved_quantity: values.approved_quantity,
        rejected_quantity: values.rejected_quantity,
      };
      await qmsApproveMutation.mutateAsync(approvalData);
      setRejectOpen(false);
    } catch {
      /* validation */
    }
  };

  const lineItemIdForForm =
    fir?.work_order_line_item_id ||
    fir?.lineItemResults?.[0]?.work_order_line_item_id ||
    '';

  const linePlanned =
    fir?.workOrderLineItem?.planned_quantity ??
    fir?.lineItemResults?.[0]?.workOrderLineItem?.planned_quantity ??
    undefined;

  const firApproval = fir ? firDerivedApproval(fir) : 'Pending Approval';
  const firApproveQtyValid = fir ? isFirApproveQuantityValid(fir) : false;
  const canApproveFir =
    !!fir && firApproval === 'Pending Approval' && fir.status === 'In Progress';
  const canClickFirApprove =
    !!fir &&
    canApproveFir &&
    firApproveQtyValid &&
    Number(fir.rejected_quantity ?? 0) <= 0;
  const canApproveQms = !!report && report.approval_status === 'Pending Approval';

  const attachmentColumnsQms: ColumnsType<NonNullable<FinalInspectionReport['attachments']>[0]> = [
    { title: 'File', dataIndex: 'file_name', key: 'file_name' },
    { title: 'Type', dataIndex: 'file_type', key: 'file_type', width: 140 },
    {
      title: 'Uploaded',
      dataIndex: 'uploaded_at',
      key: 'uploaded_at',
      render: (d: string) => new Date(d).toLocaleString(),
    },
    {
      title: 'By',
      key: 'by',
      render: (_: unknown, r: { uploaded_by?: string }) => resolve(r.uploaded_by),
    },
    {
      title: 'Actions',
      key: 'act',
      width: 120,
      render: (_: unknown, r: { id: string }) => (
        <Button size="small" danger loading={deleteQmsAtt.isPending} onClick={() => deleteQmsAtt.mutate(r.id)}>
          Remove
        </Button>
      ),
    },
  ];

  const attachmentColumnsFir: ColumnsType<FirRequestAttachment> = [
    { title: 'File', dataIndex: 'file_name', key: 'file_name' },
    { title: 'Type', dataIndex: 'file_type', key: 'file_type', width: 140 },
    {
      title: 'Uploaded',
      dataIndex: 'uploaded_at',
      key: 'uploaded_at',
      render: (d: string) => new Date(d).toLocaleString(),
    },
    {
      title: 'Actions',
      key: 'act',
      width: 120,
      render: (_: unknown, r) => (
        <Button size="small" danger loading={deleteFirAtt.isPending} onClick={() => deleteFirAtt.mutate(r.id)}>
          Remove
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      {overrideModal}
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Breadcrumb
          items={[
            { title: <Link to="/qms/dashboard">QMS</Link> },
            { title: <Link to="/qms/final-inspection-reports">Final inspection</Link> },
            { title: isQms ? report?.fir_number ?? id : fir?.fir_number ?? id },
          ]}
        />

        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col flex="auto">
            <Space align="center">
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/qms/final-inspection-reports')}>
                Back
              </Button>
              <Title level={3} style={{ margin: 0 }}>
                {isQms ? 'QMS final inspection' : 'Work order FIR'}
              </Title>
              <Tag>{source === 'qms_report' ? 'QMS report' : 'WO FIR request'}</Tag>
            </Space>
          </Col>
          <Col>
            <Space wrap>
              <Button icon={<PrinterOutlined />} onClick={() => navigate(printHref)}>
                Print preview
              </Button>
              {!isQms && fir && (
                <Link to={`/mfg/work-orders/${fir.work_order_id}`}>
                  <Button>Open work order</Button>
                </Link>
              )}
              {isQms && report && (
                <Link to={`/mfg/work-orders/${report.work_order_id}`}>
                  <Button>Open work order</Button>
                </Link>
              )}
            </Space>
          </Col>
        </Row>

        <Card
          title="Approval"
          extra={
            <Space wrap>
              {canApproveQms && (
                <>
                  <Button type="primary" icon={<CheckCircleOutlined />} onClick={openQmsApprove}>
                    Review & approve (QMS)
                  </Button>
                  <Button danger icon={<CloseCircleOutlined />} onClick={() => setRejectOpen(true)}>
                    Reject (QMS)
                  </Button>
                </>
              )}
              {!isQms && fir && fir.status === 'Pending' && (
                <Button icon={<UserAddOutlined />} onClick={() => setAssignOpen(true)}>
                  Assign inspector
                </Button>
              )}
              {canApproveFir && (
                <>
                  <Tooltip
                    title={
                      Number(fir.rejected_quantity ?? 0) > 0
                        ? 'Cannot fully approve while there is rejected quantity. Use Reject FIR instead.'
                        : !firApproveQtyValid
                          ? 'Approved + rejected must equal planned quantity (record line results first).'
                          : undefined
                    }
                  >
                    <span>
                      <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        loading={firApproveMutation.isPending}
                        disabled={!canClickFirApprove}
                        onClick={() => {
                          Modal.confirm({
                            title: 'Approve this FIR request?',
                            content: 'Quantities on the FIR must match planned quantity and reflect recorded line results.',
                            onOk: () => firApproveMutation.mutateAsync(),
                          });
                        }}
                      >
                        Approve FIR
                      </Button>
                    </span>
                  </Tooltip>
                  <Button danger icon={<CloseCircleOutlined />} onClick={() => setFirRejectOpen(true)}>
                    Reject FIR
                  </Button>
                </>
              )}
            </Space>
          }
        >
          {isQms && report && (
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Approval status">{report.approval_status}</Descriptions.Item>
              <Descriptions.Item label="Inspection status">{report.status}</Descriptions.Item>
              <Descriptions.Item label="Approved by">{resolve(report.approved_by)}</Descriptions.Item>
              <Descriptions.Item label="Approved at">
                {report.approved_at ? new Date(report.approved_at).toLocaleString() : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Rejection reason" span={2}>
                {report.rejection_reason || '—'}
              </Descriptions.Item>
            </Descriptions>
          )}
          {!isQms && fir && (
            <>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="Work order FIR lifecycle"
                description={
                  fir.status === 'Pending'
                    ? 'Assign an inspector first. The request moves to In Progress; then record line results and approve or reject.'
                    : fir.status === 'In Progress'
                      ? 'Record line item results below, then use Approve / Reject when quantities are correct.'
                      : 'This request is no longer pending action.'
                }
              />
              {fir.status === 'In Progress' && !firApproveQtyValid && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="Quantities required before approve"
                  description={
                    getFirPlannedQuantity(fir) != null
                      ? `Planned quantity is ${Number(getFirPlannedQuantity(fir)!).toFixed(4)}. Record line results so approved + rejected equals planned (both cannot be zero).`
                      : 'Record line item results with approved and/or rejected quantities that sum to the planned quantity for this line or work order.'
                  }
                />
              )}
              <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label="Request status">
                  <Tag>{fir.status}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Approval (derived)">
                  <Badge
                    status={
                      firApproval === 'Approved' ? 'success' : firApproval === 'Rejected' ? 'error' : 'warning'
                    }
                    text={firApproval}
                  />
                </Descriptions.Item>
                <Descriptions.Item label="Inspector">
                  {fir.inspector?.name || fir.inspector_name || ru(fir.inspector_id)}
                </Descriptions.Item>
                <Descriptions.Item label="Inspection date">
                  {fir.inspection_date ? new Date(fir.inspection_date).toLocaleString() : '—'}
                </Descriptions.Item>
              </Descriptions>
            </>
          )}
        </Card>

        <Tabs
          defaultActiveKey="overview"
          items={[
            {
              key: 'overview',
              label: 'Overview',
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <InspectionWorkLineDetailsCard
                    title="Work details"
                    workOrderNumber={
                      isQms
                        ? report?.work_order_number
                        : fir?.workOrder?.work_order_number || fir?.work_order_id
                    }
                    line={firWorkLine}
                    showOperations
                    operations={firLineOperations}
                    operationsLoading={boqOpsLoading}
                  />
                  {isQms && report ? (
                    <Descriptions bordered column={2}>
                      <Descriptions.Item label="FIR number">{report.fir_number}</Descriptions.Item>
                      <Descriptions.Item label="Work order">{report.work_order_number}</Descriptions.Item>
                      <Descriptions.Item label="Item" span={2}>
                        {report.item_name}
                      </Descriptions.Item>
                      <Descriptions.Item label="Inspection date">
                        {new Date(report.inspection_date).toLocaleDateString()}
                      </Descriptions.Item>
                      <Descriptions.Item label="Inspector">{report.inspector_name}</Descriptions.Item>
                      <Descriptions.Item label="Approved qty">{report.approved_quantity}</Descriptions.Item>
                      <Descriptions.Item label="Rejected qty">{report.rejected_quantity}</Descriptions.Item>
                      <Descriptions.Item label="Inspection criteria" span={2}>
                        {report.inspection_criteria?.trim() || '—'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Equipment used" span={2}>
                        {report.inspection_equipment?.length
                          ? report.inspection_equipment
                              .map((e) => `${e.asset_code} — ${e.asset_name}`)
                              .join('; ')
                          : '—'}
                      </Descriptions.Item>
                    </Descriptions>
                  ) : fir ? (
                    <Descriptions bordered column={2}>
                      <Descriptions.Item label="FIR number">{fir.fir_number}</Descriptions.Item>
                      <Descriptions.Item label="Work order">
                        {fir.workOrder?.work_order_number}
                      </Descriptions.Item>
                      <Descriptions.Item label="Requested">
                        {new Date(fir.requested_date).toLocaleString()}
                      </Descriptions.Item>
                      <Descriptions.Item label="Requested by">
                        {fir.requester?.name || ru(fir.requested_by)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Inspection criteria" span={2}>
                        {fir.inspection_criteria?.trim() || '—'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Equipment used" span={2}>
                        {fir.inspection_equipment?.length
                          ? fir.inspection_equipment
                              .map((e) => `${e.asset_code} — ${e.asset_name}`)
                              .join('; ')
                          : '—'}
                      </Descriptions.Item>
                    </Descriptions>
                  ) : null}
                </Space>
              ),
            },
            {
              key: 'inspection-details',
              label: 'Inspection details',
              children: renderInspectionDetailsTab(),
            },
            {
              key: 'attachments',
              label: 'Attachments',
              children: isQms && report ? (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Upload
                    multiple
                    fileList={uploadList.map((f, i) => ({ uid: String(i), name: f.name, status: 'done' } as const))}
                    beforeUpload={(file) => {
                      setUploadList((prev) => [...prev, file]);
                      return false;
                    }}
                    onRemove={(file) => {
                      setUploadList((prev) => prev.filter((_, i) => String(i) !== file.uid));
                    }}
                  >
                    <Button icon={<UploadOutlined />}>Select files</Button>
                  </Upload>
                  <Button
                    type="primary"
                    loading={qmsUploadMutation.isPending}
                    disabled={uploadList.length === 0}
                    onClick={async () => {
                      for (const f of uploadList) {
                        await qmsUploadMutation.mutateAsync(f);
                      }
                      setUploadList([]);
                    }}
                  >
                    Upload
                  </Button>
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={report?.attachments || []}
                    columns={attachmentColumnsQms}
                  />
                </Space>
              ) : fir ? (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Upload
                    multiple
                    fileList={uploadList.map((f, i) => ({ uid: String(i), name: f.name, status: 'done' } as const))}
                    beforeUpload={(file) => {
                      setUploadList((prev) => [...prev, file]);
                      return false;
                    }}
                    onRemove={(file) => {
                      setUploadList((prev) => prev.filter((_, i) => String(i) !== file.uid));
                    }}
                  >
                    <Button icon={<UploadOutlined />}>Select files</Button>
                  </Upload>
                  <Button
                    type="primary"
                    loading={firUploadMutation.isPending}
                    disabled={uploadList.length === 0}
                    onClick={async () => {
                      for (const f of uploadList) {
                        await firUploadMutation.mutateAsync(f);
                      }
                      setUploadList([]);
                    }}
                  >
                    Upload
                  </Button>
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={fir.attachments || []}
                    columns={attachmentColumnsFir}
                  />
                </Space>
              ) : null,
            },
            ...(isQms && report?.quality_parameters?.length
              ? [
                  {
                    key: 'params',
                    label: 'Quality parameters',
                    children: (
                      <Table
                        rowKey="id"
                        size="small"
                        pagination={false}
                        dataSource={report.quality_parameters}
                        columns={[
                          { title: 'Parameter', dataIndex: 'parameter_name' },
                          { title: 'Specification', dataIndex: 'specification' },
                          { title: 'Measured', dataIndex: 'measured_value' },
                          {
                            title: 'Result',
                            dataIndex: 'result',
                            render: (r: string) => <Tag color={r === 'Pass' ? 'green' : 'red'}>{r}</Tag>,
                          },
                          { title: 'Remarks', dataIndex: 'remarks' },
                        ]}
                      />
                    ),
                  },
                ]
              : []),
            ...(!isQms && fir
              ? [
                  {
                    key: 'lines',
                    label: 'Line results & stages',
                    children: (
                      <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        {fir.status === 'In Progress' && lineItemIdForForm && (
                          <Card title="Record line results" size="small">
                            <Form
                              form={lineForm}
                              layout="vertical"
                              onFinish={async (vals) => {
                                const a = Number(vals.approved_quantity);
                                const b = Number(vals.rejected_quantity);
                                if (a + b <= 0) {
                                  message.error('Approved and rejected quantities cannot both be zero.');
                                  return;
                                }
                                if (
                                  linePlanned != null &&
                                  Math.abs(a + b - Number(linePlanned)) > 1e-4
                                ) {
                                  message.error(
                                    `Approved (${a}) + rejected (${b}) must equal planned quantity (${Number(linePlanned).toFixed(4)}).`
                                  );
                                  return;
                                }
                                await recordLineMutation.mutateAsync({
                                  line_item_id: lineItemIdForForm,
                                  approved_quantity: a,
                                  rejected_quantity: b,
                                  rejection_reason: vals.rejection_reason || undefined,
                                });
                                lineForm.resetFields();
                              }}
                              initialValues={{
                                approved_quantity: fir.approved_quantity ?? 0,
                                rejected_quantity: fir.rejected_quantity ?? 0,
                              }}
                            >
                              <Text type="secondary">
                                Line item ID: {lineItemIdForForm}
                                {linePlanned != null ? ` · Planned qty: ${linePlanned}` : ''}
                                <br />
                                Approved + rejected must equal planned quantity (both cannot be zero). FIR approve is
                                blocked until this matches.
                              </Text>
                              <Form.Item
                                name="approved_quantity"
                                label="Approved quantity"
                                rules={[{ required: true, message: 'Required' }]}
                              >
                                <InputNumber min={0} style={{ width: '100%' }} />
                              </Form.Item>
                              <Form.Item
                                name="rejected_quantity"
                                label="Rejected quantity"
                                rules={[{ required: true, message: 'Required' }]}
                              >
                                <InputNumber min={0} style={{ width: '100%' }} />
                              </Form.Item>
                              <Form.Item name="rejection_reason" label="Rejection reason (optional)">
                                <TextArea rows={2} />
                              </Form.Item>
                              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={recordLineMutation.isPending}>
                                Save results
                              </Button>
                            </Form>
                          </Card>
                        )}
                        {Array.isArray(fir.stage_summary) && fir.stage_summary.length > 0 && (
                          <Card title="Stage summary" size="small">
                            <Collapse>
                              {fir.stage_summary.map((stage: any, index: number) => (
                                <Panel
                                  key={stage.stage_id || index}
                                  header={`${stage.stage_name} (${stage.stage_code})`}
                                >
                                  <Descriptions column={2} size="small">
                                    <Descriptions.Item label="Status">
                                      <Tag>{stage.status}</Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Sequence">{stage.sequence_number}</Descriptions.Item>
                                    {stage.started_date && (
                                      <Descriptions.Item label="Started">
                                        {new Date(stage.started_date).toLocaleString(undefined, {
                                          dateStyle: 'short',
                                          timeStyle: 'short',
                                        })}
                                      </Descriptions.Item>
                                    )}
                                    {stage.completed_date && (
                                      <Descriptions.Item label="Completed">
                                        {new Date(stage.completed_date).toLocaleString(undefined, {
                                          dateStyle: 'short',
                                          timeStyle: 'short',
                                        })}
                                      </Descriptions.Item>
                                    )}
                                    {stage.inspection && (
                                      <>
                                        <Descriptions.Item label="Inspection Number">
                                          {stage.inspection.inspection_number}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Inspection Result">
                                          <Tag color={stage.inspection.result === 'Pass' ? 'success' : 'error'}>
                                            {stage.inspection.result}
                                          </Tag>
                                        </Descriptions.Item>
                                        {stage.inspection.inspection_date && (
                                          <Descriptions.Item label="Inspection Date">
                                            {new Date(stage.inspection.inspection_date).toLocaleDateString()}
                                          </Descriptions.Item>
                                        )}
                                        {stage.inspection.status && (
                                          <Descriptions.Item label="Inspection Status">
                                            {stage.inspection.status}
                                          </Descriptions.Item>
                                        )}
                                      </>
                                    )}
                                  </Descriptions>
                                </Panel>
                              ))}
                            </Collapse>
                          </Card>
                        )}
                        <Card title="Line item results" size="small">
                          <Table<FIRLineItemResult>
                            rowKey="id"
                            size="small"
                            pagination={false}
                            dataSource={fir.lineItemResults || []}
                            columns={[
                              {
                                title: 'Line',
                                key: 'l',
                                render: (_, row) =>
                                  row.workOrderLineItem?.item_description || row.work_order_line_item_id,
                              },
                              {
                                title: 'Approved',
                                dataIndex: 'approved_quantity',
                                render: (q: number) => Number(q ?? 0).toFixed(2),
                              },
                              {
                                title: 'Rejected',
                                dataIndex: 'rejected_quantity',
                                render: (q: number) => Number(q ?? 0).toFixed(2),
                              },
                              { title: 'Reason', dataIndex: 'rejection_reason', ellipsis: true },
                            ]}
                          />
                        </Card>
                      </Space>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Space>

      <Modal
        title="Assign inspector"
        open={assignOpen}
        onCancel={() => setAssignOpen(false)}
        onOk={() => assignMutation.mutate()}
        confirmLoading={assignMutation.isPending}
      >
        <Select
          showSearch
          optionFilterProp="label"
          style={{ width: '100%' }}
          placeholder="Select user"
          value={assignInspectorId}
          onChange={setAssignInspectorId}
          options={users.map((u) => ({
            value: u.id,
            label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || u.id,
          }))}
        />
      </Modal>

      <Modal
        title="Review & approve (QMS)"
        open={qmsApproveOpen}
        onCancel={() => setQmsApproveOpen(false)}
        width={640}
        footer={[
          <Button key="c" onClick={() => setQmsApproveOpen(false)}>
            Cancel
          </Button>,
          <Button key="s" type="primary" loading={qmsApproveMutation.isPending} onClick={submitQmsApprove}>
            Approve
          </Button>,
        ]}
      >
        <Form form={approveForm} layout="vertical">
          <Form.Item
            name="approved_quantity"
            label="Approved quantity"
            rules={[{ required: true, message: 'Required' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="rejected_quantity"
            label="Rejected quantity"
            rules={[{ required: true, message: 'Required' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="approval_comments" label="Comments">
            <TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Reject (QMS)"
        open={rejectOpen}
        onCancel={() => setRejectOpen(false)}
        onOk={submitQmsReject}
        confirmLoading={qmsApproveMutation.isPending}
      >
        <Form form={approveForm} layout="vertical">
          <Form.Item name="rejection_reason" label="Rejection reason" rules={[{ required: true, message: 'Required' }]}>
            <TextArea rows={4} />
          </Form.Item>
          <Form.Item name="approval_comments" label="Comments">
            <TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Reject FIR request"
        open={firRejectOpen}
        onCancel={() => {
          setFirRejectOpen(false);
          setFirRejectReason('');
        }}
        onOk={async () => {
          if (!firRejectReason.trim()) {
            message.error('Enter a rejection reason');
            return;
          }
          await firRejectMutation.mutateAsync(firRejectReason.trim());
        }}
        confirmLoading={firRejectMutation.isPending}
      >
        <TextArea
          rows={4}
          value={firRejectReason}
          onChange={(e) => setFirRejectReason(e.target.value)}
          placeholder="Reason for rejection"
        />
      </Modal>
    </div>
  );
};

export default FinalInspectionReportDetailPage;
